import React, { createContext, useCallback, useContext, useRef } from 'react';
import ConfirmModal from '../components/ConfirmModal';
import { useConfirmAlert } from '../hooks/useConfirmAlert';

/**
 * App-wide confirmation dialog, as a promise.
 *
 * Replaces window.confirm(), which blocks the tab, ignores the app's styling
 * and cannot be tested. Wrap the tree in <ConfirmProvider> once and call it
 * from anywhere — including hooks, which have nowhere to render a modal of
 * their own:
 *
 *   const confirm = useConfirm();
 *   if (!await confirm('Delete this tag?')) return;
 *
 * `options` are passed straight to ConfirmModal: confirmText, cancelText,
 * confirmStyle.
 */
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
    const { confirmModal, setConfirmModal, closeConfirm } = useConfirmAlert();
    // The pending promise's resolver, held across the modal's lifetime.
    const resolveRef = useRef(null);

    const settle = useCallback((answer) => {
        const resolve = resolveRef.current;
        resolveRef.current = null;
        closeConfirm();
        if (resolve) resolve(answer);
    }, [closeConfirm]);

    const confirm = useCallback((message, options = {}) => new Promise((resolve) => {
        // Only one dialog can be on screen: a second request answers the first
        // with "no" rather than leaving its caller waiting forever.
        if (resolveRef.current) resolveRef.current(false);
        resolveRef.current = resolve;
        setConfirmModal({ message, ...options });
    }), [setConfirmModal]);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {confirmModal && (
                <ConfirmModal
                    message={confirmModal.message}
                    confirmText={confirmModal.confirmText || 'Confirm'}
                    cancelText={confirmModal.cancelText || 'Cancel'}
                    confirmStyle={confirmModal.confirmStyle || 'danger'}
                    onConfirm={() => settle(true)}
                    onCancel={() => settle(false)}
                />
            )}
        </ConfirmContext.Provider>
    );
}

/**
 * @returns {(message: string, options?: object) => Promise<boolean>}
 */
export function useConfirm() {
    const confirm = useContext(ConfirmContext);
    if (!confirm) {
        throw new Error('useConfirm must be used within a ConfirmProvider');
    }
    return confirm;
}

export default ConfirmContext;
